import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShoppingCart,
  TrendingUp,
  Users,
  Package,
  AlertCircle,
  Calendar,
  PackageX,
  Eye,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { StatCard } from '../components/StatCard';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { dashboardService } from '../services/dashboardService';
import { orderService } from '../services/orderService';
import { customerService } from '../services/customerService';
import { formatMoney, formatJalaliDate, formatJalaliMonth } from '../utils/form';
import type { Customer, DashboardChartPoint, DashboardStats, Order, SiteVisitMonthPoint, SiteVisitPoint } from '../types';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const emptyStats: DashboardStats = {
  totalSales: 0,
  totalIncome: 0,
  totalCogs: 0,
  totalShippingExpense: 0,
  totalPackagingCost: 0,
  totalExpenses: 0,
  totalProfit: 0,
  totalOrders: 0,
  totalCustomers: 0,
  totalProducts: 0,
  pendingOrders: 0,
  completedOrders: 0,
  cancelledOrders: 0,
  lowStockProducts: 0,
  outOfStockProducts: 0,
  chart: [],
  siteVisitsToday: 0,
  siteVisitsMonth: 0,
  siteVisitsTotal: 0,
  siteVisitsInRange: 0,
  blogViewsToday: 0,
  blogViewsMonth: 0,
  blogViewsTotal: 0,
  blogViewsInRange: 0,
  productViewsToday: 0,
  productViewsMonth: 0,
  productViewsTotal: 0,
  productViewsInRange: 0,
  siteVisitsByDay: [],
  siteVisitsByMonth: [],
  topSellingProducts: [],
  recentOrders: [],
  recentCustomers: [],
};

const statusLabel: Record<string, string> = {
  pending: 'در انتظار تایید',
  confirmed: 'در حال آماده‌سازی',
  shipped: 'ارسال شده',
  cancelled: 'لغو شده',
};

function moneyShort(value: number): string {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1_000_000) {
    return `${(n / 1_000_000).toLocaleString('fa-IR', { maximumFractionDigits: 1 })}M`;
  }
  return formatMoney(n);
}

export function DashboardPage() {
  const [dateFilter, setDateFilter] = useState('this_month');
  const [stats, setStats] = useState<DashboardStats>(emptyStats);
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [recentCustomers, setRecentCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [statsRes, ordersRes, customersRes] = await Promise.all([
          dashboardService.getStats(dateFilter),
          orderService.getOrders({ page: 1, limit: 5 }),
          customerService.getCustomers({ page: 1, limit: 5 }),
        ]);
        if (!cancelled) {
          const chart = Array.isArray(statsRes.chart) ? statsRes.chart : [];
          const siteVisitsByDay = Array.isArray(statsRes.siteVisitsByDay) ? statsRes.siteVisitsByDay : [];
          const siteVisitsByMonth = Array.isArray(statsRes.siteVisitsByMonth) ? statsRes.siteVisitsByMonth : [];
          setStats({ ...emptyStats, ...statsRes, chart, siteVisitsByDay, siteVisitsByMonth });
          setRecentOrders(ordersRes.data ?? []);
          setRecentCustomers(customersRes.data ?? []);
        }
      } catch (e) {
        if (!cancelled) {
          toast.error(e instanceof Error ? e.message : 'خطا در دریافت داشبورد');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dateFilter]);

  const chartData: DashboardChartPoint[] = (stats.chart ?? []).map((p) => ({
    ...p,
    date: formatJalaliDate(p.date),
  }));
  const visitsByDay: SiteVisitPoint[] = (stats.siteVisitsByDay ?? []).map((p) => ({
    ...p,
    date: formatJalaliDate(p.date),
  }));
  const visitsByMonth: SiteVisitMonthPoint[] = (stats.siteVisitsByMonth ?? []).map((p) => ({
    ...p,
    month: formatJalaliMonth(p.month),
  }));

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="page-title text-xl font-bold text-slate-900 sm:text-3xl">داشبورد</h1>
          <p className="text-slate-600 mt-1">آمار واقعی فروشگاه</p>
        </div>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="page-title text-xl font-bold text-slate-900 sm:text-3xl">داشبورد</h1>
          <p className="text-slate-600 mt-1">آمار واقعی بر اساس سفارش‌های غیرلغوشده</p>
        </div>
        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
        >
          <option value="today">امروز</option>
          <option value="yesterday">دیروز</option>
          <option value="7_days">۷ روز گذشته</option>
          <option value="30_days">۳۰ روز گذشته</option>
          <option value="this_month">این ماه</option>
          <option value="last_month">ماه گذشته</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="بازدید امروز سایت"
          value={(stats.siteVisitsToday ?? 0).toLocaleString('fa-IR')}
          icon={<Eye className="h-6 w-6" />}
          color="blue"
        />
        <StatCard
          title="بازدید امروز وبلاگ"
          value={(stats.blogViewsToday ?? 0).toLocaleString('fa-IR')}
          icon={<Eye className="h-6 w-6" />}
          color="green"
        />
        <StatCard
          title="بازدید امروز محصول"
          value={(stats.productViewsToday ?? 0).toLocaleString('fa-IR')}
          icon={<Eye className="h-6 w-6" />}
          color="purple"
        />
        <StatCard
          title="بازدید کل سایت"
          value={(stats.siteVisitsTotal ?? 0).toLocaleString('fa-IR')}
          icon={<Eye className="h-6 w-6" />}
          color="orange"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-2">
          <p className="text-sm font-semibold text-slate-800">سایت</p>
          <p className="text-xs text-slate-500">این ماه: {(stats.siteVisitsMonth ?? 0).toLocaleString('fa-IR')}</p>
          <p className="text-xs text-slate-500">بازه: {(stats.siteVisitsInRange ?? 0).toLocaleString('fa-IR')}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-2">
          <p className="text-sm font-semibold text-slate-800">وبلاگ</p>
          <p className="text-xs text-slate-500">این ماه: {(stats.blogViewsMonth ?? 0).toLocaleString('fa-IR')}</p>
          <p className="text-xs text-slate-500">بازه: {(stats.blogViewsInRange ?? 0).toLocaleString('fa-IR')}</p>
          <p className="text-xs text-slate-500">کل: {(stats.blogViewsTotal ?? 0).toLocaleString('fa-IR')}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-2">
          <p className="text-sm font-semibold text-slate-800">محصولات</p>
          <p className="text-xs text-slate-500">این ماه: {(stats.productViewsMonth ?? 0).toLocaleString('fa-IR')}</p>
          <p className="text-xs text-slate-500">بازه: {(stats.productViewsInRange ?? 0).toLocaleString('fa-IR')}</p>
          <p className="text-xs text-slate-500">کل: {(stats.productViewsTotal ?? 0).toLocaleString('fa-IR')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">بازدید به‌تفکیک روز</h2>
          {visitsByDay.length === 0 ? (
            <p className="text-sm text-slate-500 py-10 text-center">هنوز بازدیدی در این بازه ثبت نشده</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={visitsByDay}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis allowDecimals={false} />
                <Tooltip formatter={(v) => Number(v).toLocaleString('fa-IR')} />
                <Legend />
                <Bar dataKey="views" name="سایت" fill="#6366f1" radius={[4, 4, 0, 0]} />
                <Bar dataKey="blogViews" name="وبلاگ" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="productViews" name="محصول" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">بازدید به‌تفکیک ماه (۱۲ ماه)</h2>
          {visitsByMonth.length === 0 ? (
            <p className="text-sm text-slate-500 py-10 text-center">هنوز بازدیدی ثبت نشده</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={visitsByMonth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis allowDecimals={false} />
                <Tooltip formatter={(v) => Number(v).toLocaleString('fa-IR')} />
                <Legend />
                <Bar dataKey="views" name="سایت" fill="#6366f1" radius={[4, 4, 0, 0]} />
                <Bar dataKey="blogViews" name="وبلاگ" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="productViews" name="محصول" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="کل فروش (مبلغ)"
          value={`${moneyShort(stats.totalSales)} تومان`}
          icon={<ShoppingCart className="h-6 w-6" />}
          color="blue"
        />
        <StatCard
          title="درآمد کل"
          value={`${moneyShort(stats.totalIncome)} تومان`}
          icon={<TrendingUp className="h-6 w-6" />}
          color="green"
        />
        <StatCard
          title="سود خالص"
          value={`${moneyShort(stats.totalProfit)} تومان`}
          icon={<TrendingUp className="h-6 w-6" />}
          color="purple"
        />
        <StatCard
          title="تعداد سفارش‌ها"
          value={(stats.totalOrders ?? 0).toLocaleString('fa-IR')}
          icon={<ShoppingCart className="h-6 w-6" />}
          color="orange"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="هزینه تمام‌شده کالا"
          value={`${moneyShort(stats.totalCogs ?? 0)} تومان`}
          icon={<Package className="h-6 w-6" />}
          color="orange"
        />
        <StatCard
          title="هزینه ارسال (داخلی)"
          value={`${moneyShort(stats.totalShippingExpense ?? 0)} تومان`}
          icon={<ShoppingCart className="h-6 w-6" />}
          color="blue"
        />
        <StatCard
          title="هزینه بسته‌بندی"
          value={`${moneyShort(stats.totalPackagingCost ?? 0)} تومان`}
          icon={<Package className="h-6 w-6" />}
          color="green"
        />
        <StatCard
          title="جمع هزینه‌ها"
          value={`${moneyShort(stats.totalExpenses ?? 0)} تومان`}
          icon={<AlertCircle className="h-6 w-6" />}
          color="red"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="تعداد مشتریان"
          value={(stats.totalCustomers ?? 0).toLocaleString('fa-IR')}
          icon={<Users className="h-6 w-6" />}
          color="blue"
        />
        <StatCard
          title="تعداد محصولات"
          value={(stats.totalProducts ?? 0).toLocaleString('fa-IR')}
          icon={<Package className="h-6 w-6" />}
          color="green"
        />
        <StatCard
          title="محصولات کم‌موجودی"
          value={(stats.lowStockProducts ?? 0).toLocaleString('fa-IR')}
          icon={<AlertCircle className="h-6 w-6" />}
          color="orange"
        />
        <StatCard
          title="محصولات تمام‌شده"
          value={(stats.outOfStockProducts ?? 0).toLocaleString('fa-IR')}
          icon={<PackageX className="h-6 w-6" />}
          color="red"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-slate-600 text-sm">سفارش‌های در انتظار</p>
              <p className="page-title text-xl font-bold text-slate-900 sm:text-3xl mt-2">
                {(stats.pendingOrders ?? 0).toLocaleString('fa-IR')}
              </p>
            </div>
            <div className="h-12 w-12 rounded-lg bg-yellow-100 text-yellow-600 flex items-center justify-center">
              <Calendar className="h-6 w-6" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-slate-600 text-sm">سفارش‌های ارسال‌شده</p>
              <p className="page-title text-xl font-bold text-slate-900 sm:text-3xl mt-2">
                {(stats.completedOrders ?? 0).toLocaleString('fa-IR')}
              </p>
            </div>
            <div className="h-12 w-12 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
              <ShoppingCart className="h-6 w-6" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-slate-600 text-sm">سفارش‌های لغو شده</p>
              <p className="page-title text-xl font-bold text-slate-900 sm:text-3xl mt-2">
                {(stats.cancelledOrders ?? 0).toLocaleString('fa-IR')}
              </p>
            </div>
            <div className="h-12 w-12 rounded-lg bg-red-100 text-red-600 flex items-center justify-center">
              <AlertCircle className="h-6 w-6" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">تعداد سفارش در بازه</h2>
          {chartData.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-16">در این بازه سفارشی ثبت نشده است</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="sales" stroke="#6366f1" name="تعداد سفارش" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">درآمد و سود</h2>
          {chartData.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-16">در این بازه داده‌ای برای نمودار نیست</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip formatter={(value) => `${formatMoney(Number(value) || 0)} تومان`} />
                <Legend />
                <Bar dataKey="revenue" fill="#10b981" name="درآمد" />
                <Bar dataKey="profit" fill="#8b5cf6" name="سود" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-slate-900">آخرین سفارش‌ها</h2>
            <Link to="/product-views" className="text-sm text-indigo-600 hover:text-indigo-800">
              جزئیات بازدید محصولات
            </Link>
          </div>
          <div className="space-y-4">
            {recentOrders.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">سفارشی یافت نشد</p>
            ) : (
              recentOrders.map((order) => {
                const customer = order.customer as { name?: string; firstName?: string; lastName?: string } | undefined;
                const name =
                  customer?.name ||
                  `${customer?.firstName ?? ''} ${customer?.lastName ?? ''}`.trim() ||
                  '—';
                return (
                  <div key={order.id} className="flex items-center justify-between pb-4 border-b border-slate-100 last:border-0">
                    <div>
                      <p className="font-medium text-slate-900">{name}</p>
                      <p className="text-xs text-slate-500">{order.orderNumber}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-slate-900">
                        {formatMoney(order.finalAmount ?? 0)} تومان
                      </p>
                      <span className="inline-block px-2 py-1 text-xs rounded-full bg-slate-100 text-slate-700 mt-1">
                        {statusLabel[order.orderStatus] ?? order.orderStatus}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">مشتریان جدید</h2>
          <div className="space-y-4">
            {recentCustomers.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">مشتری‌ای یافت نشد</p>
            ) : (
              recentCustomers.map((customer) => (
                <div key={customer.id} className="flex items-center justify-between pb-4 border-b border-slate-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold">
                      {customer.name?.charAt(0) || '—'}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{customer.name || '—'}</p>
                      <p className="text-xs text-slate-500">{customer.phone || customer.email || '—'}</p>
                    </div>
                  </div>
                  <p className="font-medium text-slate-900">
                    {formatMoney(customer.totalSpent ?? 0)} تومان
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
